import type { BusinessCategory } from '@/components/public/CreateYourPage'

// Escola (ou página de criador) como o diretório público (/explore) a consome.
// É um subconjunto do que a landing recebe: só o que cabe num card e num pino de
// mapa. String, não number: o card une School e Page na mesma lista/mapa, e o
// backend prefixa o id de cada uma ("s7"/"p7") para as duas nunca colidirem como
// chave React ou id de marcador do Leaflet.
export interface DirectorySchool {
  id: string
  name: string
  slug: string | null
  /**
   * Caminho da landing da escola, ou null quando ela não tem página pública
   * ligada (ou não tem slug). O card decide entre <a> e <div> por este campo —
   * um booleano exigiria duplicar a regra no frontend.
   */
  href: string | null
  avatar_url: string | null
  /** Galeria da página pública, no máximo 5, para o carrossel do card. */
  photos: string[]
  city: string | null
  state: string | null
  address: string | null
  tagline: string | null
  /** Número, não string: o backend faz .to_f porque a coluna é decimal. */
  latitude: number | null
  longitude: number | null
  features: string[]
  /** Categoria do negócio. Já vinha do servidor e faltava aqui. */
  business_category: BusinessCategory | null
  /** Serviço declarado ("Aula de Surf") — a etiqueta do card na vista lista. */
  service_label: string | null
  primary_color: string | null
  /** Distância até o visitante, em km. Só quando ele autorizou a localização. */
  distance_km: number | null
}

/**
 * Escola na vitrine "Quem usa BeWave", na home. É o menor recorte de todos: só o
 * que cabe numa logo com nome embaixo.
 */
export interface ShowcaseSchool {
  id: number
  name: string
  /** Null quando a escola não tem slug — a logo aparece, mas sem link. */
  href: string | null
  /** Null quando a escola não subiu logo; a vitrine cai nas iniciais. */
  avatar_url: string | null
}

export interface CityOption {
  slug: string
  label: string
  count: number
}

/** Quantas escolas têm cada selo, calculado sobre a lista NÃO filtrada. */
export interface FeatureFacet {
  key: string
  count: number
}

/**
 * O mesmo para as 7 categorias (a divisão do catálogo).
 *
 * `BusinessCategory` vem de CreateYourPage.tsx, e não daqui, porque um teste de
 * model (test/models/school_test.rb) lê aquele arquivo para conferir se as
 * chaves batem com o enum `School.business_category`. Mover o tipo quebraria a
 * checagem sem ganho nenhum.
 */
export interface CategoryFacet {
  key: BusinessCategory
  count: number
}

/**
 * Um dos 32 serviços do catálogo (Surf, Pousadas, Shapers…), com quantos
 * negócios o oferecem. É por eles que o /explore filtra.
 *
 * Traz o `label` junto (ao contrário de `CategoryFacet`, cujo rótulo está no
 * catálogo do frontend): são 32, mudam por seed sem deploy, e duplicar essa
 * lista no cliente só criaria uma segunda verdade para sair de sincronia.
 */
export interface ServiceFacet {
  key: string
  label: string
  /** Webp em /explore/taxonomia — o card do Figma. Null enquanto a seed não rodou. */
  image_path: string | null
  count: number
}

/** Área visível do mapa, quando a busca veio de arrastar ou dar zoom. */
export interface MapBounds {
  ne_lat: number
  ne_lng: number
  sw_lat: number
  sw_lng: number
}

/** Escola que tem coordenadas — o mapa só lida com estas. */
export type LocatedSchool = DirectorySchool & {
  latitude: number
  longitude: number
}

export function hasCoordinates(
  school: DirectorySchool,
): school is LocatedSchool {
  return school.latitude !== null && school.longitude !== null
}
